/**
 * Blog post: Online mock exams vs. the real German exam.
 * SEO target: "german mock exam online", "mock exam vs real exam",
 * "exam simulator telc/goethe", "are mock exams accurate".
 * No single provider/level, so no RelatedResources; internal links go to
 * the mock-exams hub and the free trial instead.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function MockVsRealExamPost() {
  const lang = useLangParam();

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Every candidate asks the same question before booking a telc, Goethe, or ÖSD exam: if I pass the
        mock, will I pass the real thing? The honest answer is yes, with conditions. This article explains
        exactly what an online mock exam replicates, what it cannot replicate, and how to use that
        difference to walk into the exam room already knowing your result.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        What a good online mock exam gets identical
      </h2>
      <ul className="list-disc pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li>
          <strong>The format.</strong> Sections, task types, question counts, and the order they appear in
          follow the official specimen papers. A telc B2 Leseverstehen has the same parts online as on paper.
        </li>
        <li>
          <strong>The timing.</strong> The clock is the single biggest surprise of a real exam. A serious
          simulator enforces official section timings, including the moment when time simply runs out.
        </li>
        <li>
          <strong>The scoring logic.</strong> Reading and listening are objectively scored, so a digital
          score matches a paper score. For writing, Logami scores your text against criteria modelled on the
          official examiner rubric: content, communicative design, and accuracy.
        </li>
        <li>
          <strong>The difficulty.</strong> Mock papers built from the official specimen logic sit at the same
          CEFR level as the real exam. If you consistently score 75 percent online, you are not a borderline
          candidate.
        </li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        What is genuinely different on exam day
      </h2>
      <ul className="list-disc pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li>
          <strong>Paper and handwriting.</strong> In the real exam you write your letter by hand. Practice
          at least two writing tasks on paper before exam day so your hand knows the pace.
        </li>
        <li>
          <strong>The room.</strong> Other candidates, invigilators, ID checks, and fixed breaks add stress
          that no simulator can fully reproduce. This is exactly why timed mocks matter: the more familiar
          the tasks feel, the less the environment matters.
        </li>
        <li>
          <strong>Speaking with a partner.</strong> telc oral exams are usually taken in pairs. Use the mock
          to master the structure and Redemittel, then rehearse the dialogue parts with a partner or tutor.
        </li>
        <li>
          <strong>AI feedback versus human raters.</strong> AI scoring is calibrated to the official
          criteria and is consistent, but the certificate score comes from trained human raters. Treat the
          AI score as a reliable indicator, not a guarantee.
        </li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The readiness rule of thumb
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Passing requires 60 percent in every scored part, at every provider. Because exam-day stress
        typically costs a few points, use this margin rule: you are ready to book the exam when you score
        <strong> 70 to 75 percent or higher in three consecutive full timed simulations</strong>, with no
        single section below 65 percent. One strong mock can be luck; three in a row is a level.
      </p>
      <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] mb-10">
        <h3 className="font-space text-lg font-bold text-black mb-3">A simple four-week protocol</h3>
        <ol className="list-decimal pl-5 text-base text-black/70 leading-relaxed space-y-2">
          <li>Week 1: one full mock to find your baseline and weakest section</li>
          <li>Weeks 2 and 3: targeted section training plus one full mock each week</li>
          <li>Week 4: one final mock under strict conditions, then rest before exam day</li>
        </ol>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Try the difference yourself
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        The fastest way to judge a simulator is to sit one. You can start a
        {' '}<Link to={`/${lang}/try`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">full mock exam for free, without an account</Link>,
        in the official format for telc, Goethe, or ÖSD at B1 to C1. The
        {' '}<Link to={`/${lang}/mock-exams`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">mock exam overview</Link>{' '}
        lists every exam with its exact sections, timings, and the number of complete practice papers
        available.
      </p>
    </article>
  );
}
